'use client';

import { useEffect, useRef } from 'react';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';
import { useAppSelector } from 'src/app';
import { getUserHeaderInfo } from 'src/app/reducers/userSelector';
import { pushAuthState } from '@utils/analytics/ga4-auth-state';

// Module-level state to survive React StrictMode double-mounts and multiple component instances
let lastAuthPush: { status: 'authenticated' | 'unauthenticated'; userId?: number } | undefined;

/**
 * @description Watches auth changes and pushes an auth_state event to dataLayer for GA4 user_id handling via GTM.
 * - Uses cookie presence (shallow auth) + HeaderInfo.userId (stable id) to decide when to set/clear user_id.
 * - Never sends null/empty/"null"; clears user_id on logout.
 * - Guards against duplicate pushes across re-mounts and multiple instances.
 */
export const useGa4UserId = () => {
  const { isAuth, checkAuthCookie } = useAuthClient();
  const headerInfo = useAppSelector(getUserHeaderInfo);
  const lastStateRef = useRef<{ status: 'authenticated' | 'unauthenticated'; userId?: number } | undefined>(undefined);

  useEffect(() => {
    // Read current signals
    const cookieAuth = checkAuthCookie();
    const userId = headerInfo?.userId;

    // If cookie indicates an authenticated session but userId is not yet available, wait to avoid flip-flop events
    if (cookieAuth && userId == null) {
      return;
    }

    const current = cookieAuth && userId != null
      ? { status: 'authenticated' as const, userId }
      : { status: 'unauthenticated' as const };

    // Avoid duplicate pushes (per instance and globally across remounts)
    const sameAsRef = lastStateRef.current?.status === current.status && (current.status === 'unauthenticated' || lastStateRef.current?.userId === current.userId);

    const sameAsGlobal = lastAuthPush?.status === current.status && (current.status === 'unauthenticated' || lastAuthPush?.userId === current.userId);

    if (sameAsRef || sameAsGlobal) {
      return;
    }

    if (current.status === 'authenticated') {
      pushAuthState('authenticated', current.userId);
    } else {
      pushAuthState('unauthenticated');
    }

    lastStateRef.current = current;
    lastAuthPush = current;
  }, [isAuth, headerInfo?.userId, checkAuthCookie]);
};
